export default function PrivacyPolicyContentEn() {
  return (
    <div className="px-4">
      <h1 className="text-xl sm:text-2xl font-bold text-dark mb-4">Personal Data Protection Policy</h1>

      <p className="text-sm text-dark leading-relaxed mb-3">
        Thai Honda Co., Ltd. (&quot;Honda,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is
        committed to maintaining your privacy. We have prepared this Personal Data Protection Policy
        (&quot;Policy&quot;) to provide details regarding the collection, use, or disclosure of your
        personal data (collectively referred to as &quot;processing&quot;), whether through direct
        communication with us or through other channels, such as visiting our website or
        downloading/using Honda applications (&quot;Honda Apps&quot;). Honda processes your personal
        data within the framework prescribed by the Personal Data Protection Act B.E. 2562 (2019)
        (&quot;PDPA&quot;) and other relevant laws.
      </p>

      <h2 id="privacy-section-1" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        1. Scope of Application
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        &quot;Personal Data&quot; herein refers to information that can directly or indirectly
        identify a living individual. This Policy covers all processing of personal data conducted
        by Honda for the following individuals:
      </p>
      <ol className="list-decimal ml-4 mb-3">
        <li className="text-sm text-dark leading-relaxed mb-2">
          Individual customers (past, present, or future), including legal representatives of
          corporate customers.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Employees, officers, and personnel currently, previously, or seeking to be affiliated with
          Honda (permanent or temporary).
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Individual shareholders and legal representatives of corporate shareholders.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Managing directors, authorized persons, and attorneys-in-fact of Honda.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Partners, contractors, business allies, and vendors (individuals or corporate
          representatives).
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Service providers, lessors/lessees, and licensors/licensees (individuals or corporate
          representatives).
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Outsourced workers, probationers, and interns.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Participants in activities organized by or in collaboration with Honda.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Individuals whose data was disclosed to Honda by other data controllers or processors.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          Any other joint owners of personal data belonging to the persons mentioned above.
        </li>
      </ol>
      <p className="text-sm text-dark leading-relaxed mb-3">
        Additionally, this Policy applies to processing that occurs on the website, official online
        communication channels, mobile applications, customer relations centers, activities and
        exhibitions, and any other methods through which Honda processes personal data. However,
        this Policy does not cover personal data you provide to us through other channels not under
        our direct control, such as social media networks (e.g., Facebook, Line, Instagram,
        Twitter, etc.), nor does it cover cases where you purchase products or use services from
        dealers, individuals, or entities certified by us. In such cases, if you wish to know the
        details of data processing or exercise your rights under personal data protection laws,
        please contact those individuals or entities directly.
      </p>
      <p className="text-sm text-dark leading-relaxed mb-3">
        In the case of personal data we collected before the personal data protection law came
        into effect, Honda reserves the right to continue processing that personal data for its
        original purpose without requiring your consent. However, once the personal data protection
        law is in effect, if you do not wish for us to continue collecting and using your personal
        data for such purposes, you may contact us to stop the collection or use of that personal
        data via the contact channels specified in Section 12: Contact Us.
      </p>

      <h2 id="privacy-section-2" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        2. Personal Data We Collect
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">We may collect the following data:</p>
      <ul className="list-disc ml-4 mb-3">
        <li className="text-sm text-dark leading-relaxed mb-1">
          Personal Info: Name, surname, ID number, and date of birth.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Contact Info: Address, phone number, email, and LINE ID.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Profile Info: Username, password, or PIN.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Vehicle Info: Vehicle Identification Number (VIN), purchase date, and dealership info.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Product Info: Product codes, warranty details, and purchase location.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Transaction Info: Purchase/service history, product specifications, bank account numbers,
          and billing addresses.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Feedback/Interests: Opinions, suggestions, chat logs, and survey responses.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Employment Info: Photos, educational history, work experience, and special skills.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Details regarding the connection to the Honda Website or Honda Apps: such as your
          operating system, your browser type, details of the device you use (e.g., serial number,
          MAC Address), and geographical location of the connection.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Details regarding visits to the Honda Website or Honda Apps: such as cookies, IP Address,
          or unique identifiers of the tool or device used for connection (collectively referred to
          as &quot;Device&quot;), time of visit, and duration of the connection.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          Other information you send to us in connection with the Honda Website or Honda Apps: such
          as your location.
        </li>
      </ul>

      <h2 id="privacy-section-3" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        3. Purposes for Collection
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We may collect, use, and disclose your personal data for the following purposes:
      </p>
      <ol className="list-decimal ml-4 mb-3">
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Business Management:</strong> To manage the business operations of Honda and its
          affiliates; to support and carry out activities related to the booking and sale of
          products/services, such as financial transactions, account management, verification and
          confirmation of actions, delivery, warranties, membership management or registration for
          benefits, facilitating registration/insurance, refunds or product exchanges, after-sales
          services, and various management tasks to facilitate your use of products/services; to
          communicate with you; and to provide special privileges, promotions, marketing news
          regarding Honda products and services, benefits, lucky draws, prize drawings, competitions,
          activity participation, and training seminars as a valued Honda customer.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Dealer Support:</strong> To perform various actions as a distributor of Honda
          motorcycles in Thailand and to support and develop business for the benefit of Honda and
          its affiliates; and to support the business operations of appointed dealers, including
          management, contact, coordination, support, and business promotion in various forms, as
          well as any transactions or legal acts related or potentially related to the dealers.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Marketing, Personalization, and Data Analytics:</strong> For public relations and
          sales promotion of products and services; providing information, news, and benefits to
          recommend products/services that are useful to you or in which you may be interested. In
          this regard, we may provide a personalized experience by analyzing data based on your
          interests or behavior regarding products/services (Profiling) and conducting Data
          Analytics from the processing of your personal data to learn more about your use of
          products and services, and other products/services you may be interested in; to evaluate
          your interest or behavior toward products and services; to recommend products and
          services you may be interested in; to provide an experience appropriate for you; and to
          conduct market research, surveys, and evaluations of behavior, statistics, and
          classification, consumption patterns and trends, to develop data quality and increase the
          efficiency of sales promotions and various products and services. For such purposes, we
          will collect and process your personal data for your interests and benefits, and for the
          legitimate interests and business operations of ourselves, our affiliates, the Honda
          Group, and/or our business partners, which do not exceed the fundamental rights to
          personal data of the data subject. We will seek your consent from time to time as
          necessary.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Product and Service Improvement:</strong> To develop and improve Honda
          products/services and the various appointed or authorized distribution and service centers
          by requesting opinions or satisfaction feedback regarding products/services received from
          us or from the distribution and service centers of our appointed dealers, in order to
          consider the effectiveness of those centers regarding customer service so that customers
          receive appropriate service. For such purposes, we will collect and disclose your
          personal data for your benefit, and for the legitimate interests and business operations
          of ourselves, our affiliates, the Honda Group, and/or our business partners, which do not
          exceed the fundamental rights to personal data of the data subject. We will seek your
          consent from time to time as necessary.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Operational Efficiency:</strong> To manage and develop the efficiency of core
          business operations; to conduct research and development of services, products, systems,
          and business operations for ourselves, our affiliates, and the Honda Group by considering
          the overview of data, business feasibility and appropriateness, and problem analysis and
          resolution.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Human Resources:</strong> For employment, recruitment and selection of personnel,
          management of human resources and organizational welfare for ourselves, our affiliates,
          and the Honda Group, and any actions related to personnel administration according to
          organizational and Honda Group policies. This objective for collecting and processing data
          is for employment purposes under labor laws only.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Activities and Media:</strong> To conduct activities, competitions, or various
          projects organized by Honda, or in which Honda is a part or participant; including
          participation in such competitions, projects, or activities; and the processing of still
          images and motion pictures through our various communication channels or the mass media.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Contractual Obligations:</strong> To enter into contracts, agreements, or memorandums
          of understanding (MOU) to comply with agreements, conditions, or contractual obligations made
          with you.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>IT and Platform Management:</strong> To manage websites and applications to
          facilitate smooth access and usage; to customize usage patterns to match your interests; to
          develop efficiency in information technology security; including internal business management
          according to internal regulations, policies, and processes.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Legal Compliance:</strong> To perform duties according to relevant laws or
          regulations, both for business operations—such as industrial standards and accounting—or as
          required by law to protect your rights and interests, as well as those of ourselves, our
          affiliates, and the Honda Group.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Identity Verification:</strong> To verify and authenticate identity according to
          legal requirements or other applicable regulations, or by methods used for the security of
          your personal data.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Business Transitions:</strong> To use, share, or transfer personal data related to
          mergers, acquisitions, rights holdings, joint ventures, sales of Honda assets,
          consolidations, restructuring, financing, business asset transactions, or other types of
          acquisitions or business combinations of all or part of our assets, or the transfer of all
          or part of the business to relevant companies.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Risk Management:</strong> For risk management, operational auditing, and risk
          assessment; and/or
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Security:</strong> To record and monitor the safety of employees and individuals
          entering Honda premises, including protecting Honda&apos;s property through closed-circuit
          television or CCTV.
        </li>
      </ol>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We are aware that your personal data processed for purposes related to legal compliance or
        entering into/performing any contracts under the objectives in this section is necessary for
        us to achieve such purposes. Choosing not to share your personal data with us or withdrawing
        your consent for the use of your personal data may have legal consequences or result in our
        inability to perform contractual obligations or enter into a contract between us and you,
        as the case may be. In such instances, we may find it necessary to refuse to enter into a
        contract with you or to cease any operations, whether in whole or in part, for you.
      </p>

      <h2 id="privacy-section-4" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        4. Methods of Personal Data Collection
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        When necessary, we will request your consent before or at the time of collecting your
        personal data in either physical or electronic formats. If these formats are not feasible,
        we may request consent through other methods, such as verbal consent via an automated voice
        recording system. However, we may collect your personal data without prior consent in the
        following cases:
      </p>
      <ul className="list-disc ml-4 mb-3">
        <li className="text-sm text-dark leading-relaxed mb-1">
          <strong>Historical or Research Purposes:</strong> For the preparation of historical
          documents or archives for the public interest, or related to research or statistics. We
          will implement appropriate security measures to protect your rights and freedoms in
          accordance with the law and notifications from the Personal Data Protection Committee.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          <strong>Vital Interests:</strong> To prevent or suppress a danger to a person&apos;s life,
          body, or health.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          <strong>Contractual Necessity:</strong> For the performance of a contract to which you are a
          party, or to take steps at your request prior to entering into a contract.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          <strong>Public Task:</strong> When necessary for the performance of a task carried out in
          the public interest or the exercise of official authority vested in us by the state.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          <strong>Legitimate Interests:</strong> When necessary for our legitimate interests or those
          of another person or legal entity, provided that such interests do not override your
          fundamental rights regarding personal data.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-1">
          <strong>Legal Obligations:</strong> To comply with laws applicable to Honda.
        </li>
      </ul>

      <h2 id="privacy-section-5" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        5. Collection of Sensitive Data
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        If it is necessary to collect sensitive data—such as religion, blood type, race, health
        data, fingerprints, disability, labor union information, criminal records, or similar data
        as specified by law—for your benefit, we will request your explicit consent. This request
        may be made in physical or electronic form, except where consent is not required by law. You
        may choose whether or not to disclose this sensitive data. However, if the data is marked as
        &quot;required,&quot; we inform you that it is collected for special support purposes, such as
        preparing appropriate food or facilities, allocating welfare, or providing medical
        treatment.
      </p>

      <h2 id="privacy-section-6" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        6. Retention Period
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We will retain your personal data only for as long as necessary within a reasonable period or
        as required by law to achieve the purposes specified in this Policy and for the specific
        protection of your personal data.
      </p>

      <h2 id="privacy-section-7" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        7. Storage Location
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We store your personal data in Thailand. However, if necessary to achieve the objectives
        stated in this Policy, we may store your data in databases located abroad. In such cases, we
        will ensure that your personal data receives appropriate protection under personal data
        protection laws.
      </p>

      <h2 id="privacy-section-8" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        8. Disclosure and Transfer of Personal Data
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        Your personal data is treated as confidential. Transferring data to others—including our
        affiliates, authorized persons, business partners, contractors, service providers,
        accountants, auditors, consultants, lawyers, and specialists who may be located in Thailand or
        abroad—will be done carefully. This disclosure is strictly to achieve the purposes stated in
        this Policy. We will ensure the recipient maintains adequate data protection standards as
        required by law.
      </p>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We may also share data with government authorities and relevant persons to perform legal
        duties or upon lawful request. Furthermore, during business transitions, we may share or
        transfer data to potential partners related to mergers, acquisitions, joint ventures, asset
        sales, or restructuring.
      </p>

      <h2 id="privacy-section-9" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        9. Security Measures
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We have established appropriate organizational, technical, and administrative measures to
        protect personal data under our control from unauthorized destruction, loss, access, use,
        alteration, or disclosure. While no internet transmission is perfectly secure, we continuously
        develop our systems to align with current technology.
      </p>

      <h2 id="privacy-section-10" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        10. Updates to the Personal Data Protection Policy
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We may update this Policy from time to time to comply with relevant laws or regulations.
        Significant changes will be notified via our website, applications, or other official
        online channels. Choosing not to consent to such changes may result in the inability to
        receive certain benefits or use all or part of our products and services.
      </p>

      <h2 id="privacy-section-11" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        11. Your Legal Rights
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        Under this Policy, you have the following legal rights regarding your personal data:
      </p>
      <ul className="list-disc ml-4 mb-3">
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to Withdraw Consent:</strong> You may withdraw consent at any time, though
          this may affect your ability to receive certain services. This does not affect processing
          already carried out based on prior consent.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to Access:</strong> You may request access to, a copy of, or disclosure of how
          we obtained your personal data.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to Restriction:</strong> You may request to restrict the use of your personal
          data.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to Data Portability:</strong> You may request to receive your data in a
          machine-readable format or have it transferred directly to another data controller where
          technically feasible.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to Object:</strong> You may object to the processing of your personal data.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to be Forgotten (Erasure):</strong> You may request that we delete, destroy,
          or anonymize your personal data.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to Rectification:</strong> You may request that we correct your data to be
          accurate, current, complete, and not misleading.
        </li>
        <li className="text-sm text-dark leading-relaxed mb-2">
          <strong>Right to Lodge a Complaint:</strong> You may complain to relevant legal authorities
          if you believe data protection laws have been violated.
        </li>
      </ul>
      <p className="text-sm text-dark leading-relaxed mb-3">
        We reserve the right to refuse requests to maintain your benefits, to comply with the law, or
        if you cannot verify your identity as the data subject. Fees may apply for certain requests at
        Honda&apos;s discretion. For services purchased from certified third-party dealers, please
        contact them directly as they are your primary data controllers.
      </p>

      <h2 id="privacy-section-12" className="text-base sm:text-lg font-bold text-dark mb-3 mt-6">
        12. Contact Us
      </h2>
      <p className="text-sm text-dark leading-relaxed mb-3">
        For inquiries regarding this Policy, please contact:
      </p>
      <div className="text-sm text-dark leading-relaxed mb-3 pl-4">
        <p className="mb-1">
          <strong>Thai Honda Co., Ltd. (Head Office)</strong>
        </p>
        <p className="mb-1">410 Lat Krabang Industrial Estate, Chalong Krung Road,</p>
        <p className="mb-1">Lam Pla Thiu Subdistrict, Lat Krabang District, Bangkok 10520</p>
        <p className="mb-1">Email: Privacy@honda.th.com</p>
        <p className="mb-1">Call Center: 02-7254000</p>
      </div>
    </div>
  );
}
